import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/UI/Button';
import { Input } from '../components/UI/Input';

export function Signup() {
  const navigate = useNavigate();
  const { signup, user } = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [signupComplete, setSignupComplete] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!email || !password || !confirmPassword) {
      setError('Please fill in all fields');
      return;
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    try {
      setIsLoading(true);
      await signup(email, password);
      setSignupComplete(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Signup failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Show approval pending message after signup
  if (signupComplete) {
    return (
      <Layout>
        <div className="min-h-[calc(100vh-64px)] flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
          <div className="max-w-md w-full space-y-8">
            <div className="bg-white border border-gray-200 py-8 px-6 rounded-xl shadow-sm">
              <div className="text-center">
                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-amber-50 border border-amber-200">
                  <svg className="h-6 w-6 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <h2 className="mt-4 text-xl font-bold text-gray-900">Account Created</h2>
                <div className="mt-4 bg-amber-50 border border-amber-200 text-amber-800 px-4 py-3 rounded-lg text-sm">
                  Your account has been created but requires admin approval before you can place orders.
                  You can still log in and view the platform.
                </div>
                <div className="mt-6 space-y-3">
                  <Link
                    to="/dashboard"
                    className="block w-full bg-brand-500 text-white hover:bg-brand-600 px-4 py-2 rounded-lg text-sm font-medium transition-colors text-center"
                  >
                    Go to Dashboard
                  </Link>
                  <Link
                    to="/"
                    className="block text-sm font-medium text-gray-500 hover:text-gray-700 transition-colors"
                  >
                    Back to home
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="min-h-[calc(100vh-64px)] flex">
        {/* Left panel — branding (hidden on mobile) */}
        <div className="hidden lg:flex lg:w-1/2 bg-gray-50 relative overflow-hidden flex-col justify-center px-16">
          <div className="absolute inset-0 dot-grid opacity-40" />
          <div className="absolute top-0 left-0 w-[400px] h-[400px] bg-brand-100/30 rounded-full blur-[120px]" />
          <div className="relative z-10">
            <p className="text-brand-600 font-mono text-sm tracking-widest uppercase mb-4">
              Get Started
            </p>
            <h2 className="text-4xl font-bold text-gray-900 tracking-tight mb-4">
              Start Building<br />in Minutes
            </h2>
            <p className="text-gray-500 text-lg mb-12 max-w-md">
              Create your account and integrate with our US equity trading infrastructure.
            </p>
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <div className="w-6 h-6 rounded-full bg-brand-50 border border-brand-200 flex items-center justify-center">
                  <span className="text-brand-600 text-xs font-mono">1</span>
                </div>
                <span className="text-gray-600 text-sm">Create your account</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-6 h-6 rounded-full bg-brand-50 border border-brand-200 flex items-center justify-center">
                  <span className="text-brand-600 text-xs font-mono">2</span>
                </div>
                <span className="text-gray-600 text-sm">Get API credentials</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-6 h-6 rounded-full bg-brand-50 border border-brand-200 flex items-center justify-center">
                  <span className="text-brand-600 text-xs font-mono">3</span>
                </div>
                <span className="text-gray-600 text-sm">Start trading</span>
              </div>
            </div>
          </div>
        </div>

        {/* Right panel — signup form */}
        <div className="flex-1 bg-white flex items-center justify-center px-4 sm:px-6 lg:px-8 py-12">
          <div className="max-w-md w-full space-y-8">
            <div>
              <h2 className="text-center text-3xl font-bold text-gray-900 tracking-tight">
                Create your account
              </h2>
              <p className="mt-2 text-center text-sm text-gray-500">
                Already have an account?{' '}
                <Link
                  to="/login"
                  className="font-medium text-brand-600 hover:text-brand-500 transition-colors"
                >
                  Sign in
                </Link>
              </p>
            </div>

            <div className="bg-white border border-gray-200 py-8 px-6 rounded-xl shadow-sm">
              <form onSubmit={handleSubmit} className="space-y-6">
                {error && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                    {error}
                  </div>
                )}

                <Input
                  label="Email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                  autoComplete="email"
                  disabled={isLoading}
                />

                <Input
                  label="Password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  autoComplete="new-password"
                  helperText="Must be at least 8 characters"
                  disabled={isLoading}
                />

                <Input
                  label="Confirm Password"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  autoComplete="new-password"
                  disabled={isLoading}
                />

                <Button
                  type="submit"
                  fullWidth
                  isLoading={isLoading}
                >
                  Create Account
                </Button>
              </form>
            </div>

            <div className="text-center text-sm">
              <Link
                to="/"
                className="font-medium text-gray-500 hover:text-gray-700 transition-colors"
              >
                &larr; Back to home
              </Link>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
